import _ from "lodash"

const INCOMPLETE = 0
const GREAT_STRUGGLE = 1
const STRUGGLE = 2
const NO_STRUGGLE = 3

// Accepts a WorkProductSummary object.
// Returns a dup of the object with a new key, 'grade', who's value is either 1 for "great struggle", 2 for "struggle", or 3 for "no struggle".
//
// For each component function, version is ignored unless multiple versions of a grading scheme exist. Version is intended
// to maintain compatibility with older WorkProductSummary rows when game mechanics changes require fundamental
// changes in how we grade summary data.
const gradeSummary = summary => {
  const { summary_data, work_product_type, version } = summary

  const gradeArgumentBuilderSummary = ({ correct_count, neutral_count, total_count }, version) => {
    const argGrade = correct_count / parseFloat(total_count - neutral_count)
    if (argGrade >= 0.5) {
      return 3
    } else if (argGrade > 0.2) {
      return 2
    } else {
      return 1
    }
  }

  const gradeCategorizationSummary = ({ correct_count, total_count }, version) => {
    const catGrade = correct_count / parseFloat(total_count)
    if (catGrade >= 0.9) {
      return 3
    } else if (catGrade > 0.7) {
      return 2
    } else {
      return 1
    }
  }

  const gradeResearchSummary = (summaryData, version) => {
    if (version === 1) {
      const { correct_count, total_count } = summaryData
      const researchGrade = correct_count / parseFloat(total_count)
      if (researchGrade >= 0.7) {
        return 3
      } else if (researchGrade >= 0.5) {
        return 2
      } else {
        return 1
      }
    } else if (version === 2) {
      const { correct_count, total_selection_count } = summaryData
      const researchGrade = correct_count / parseFloat(total_selection_count)
      if (researchGrade >= 0.7) {
        return 3
      } else if (researchGrade >= 0.5) {
        return 2
      } else {
        return 1
      }
    } else {
      console.log("Error determining version for research task: " + version)
    }
  }

  const gradeFlowchartSummary = (summaryData, version) => {
    if (version === 1) {
      const { total_point_count, points_required, total_submission_count } = summaryData
      const flowGrade =
        total_point_count / parseFloat(points_required) / parseFloat(total_submission_count)
      if (flowGrade >= 0.7) {
        return 3
      } else if (flowGrade >= 0.5) {
        return 2
      } else {
        return 1
      }
    } else if (version === 2) {
      const { total_point_count, points_required, total_submission_count, incorrect_count } =
        summaryData
      // The percentage of how much incorrect answers are worth relative to correct answers. Ie, if the value is 0.5,
      // incorrect answers have half the impact on the overall score than correct answers do
      const incorrectWeight = 0.3
      const correctRatio = total_point_count / parseFloat(points_required)
      const incorrectRatio = incorrectWeight * (incorrect_count / parseFloat(points_required))
      const flowGrade = (correctRatio - incorrectRatio) / parseFloat(total_submission_count)
      if (flowGrade >= 0.7) {
        return 3
      } else if (flowGrade >= 0.5) {
        return 2
      } else {
        return 1
      }
    } else {
      console.log("Error determining version for flowchart task: " + version)
    }
  }

  const gradePictureClickSummary = ({ correct_count, total_count }, version) => {
    const picGrade = correct_count / parseFloat(total_count)
    if (picGrade >= 0.85) {
      return 3
    } else if (picGrade >= 0.7) {
      return 2
    } else {
      return 1
    }
  }

  const gradeHeredityProbabilitySummary = ({ correct_count, total_count }, version) => {
    const probGrade = correct_count / parseFloat(total_count)
    if (probGrade >= 0.9) {
      return 3
    } else if (probGrade >= 0.75) {
      return 2
    } else {
      return 1
    }
  }

  const gradeFrequencyChartSummary = ({ incorrect_count, total_count }, version) => {
    switch (true) {
      case incorrect_count >= 5:
        return GREAT_STRUGGLE
      case _.inRange(incorrect_count, 2, 5):
        return STRUGGLE
      case _.inRange(incorrect_count, 0, 2):
        return NO_STRUGGLE
      default:
        return INCOMPLETE
    }
  }

  let grade
  switch (work_product_type) {
    case "Argument Builder":
      grade = gradeArgumentBuilderSummary(summary_data, version)
      break
    case "Categorization":
      grade = gradeCategorizationSummary(summary_data, version)
      break
    case "Research":
      grade = gradeResearchSummary(summary_data, version)
      break
    case "Flowchart":
      grade = gradeFlowchartSummary(summary_data, version)
      break
    case "Picture Click":
      grade = gradePictureClickSummary(summary_data, version)
      break
    case "Heredity Probability":
      grade = gradeHeredityProbabilitySummary(summary_data, version)
      break
    case "Frequency Chart":
      grade = gradeFrequencyChartSummary(summary_data, version)
  }

  return Object.assign({ grade }, summary)
}

// Accepts an array of WorkProductSummary objects.
// Returns an array of objects where each object represents a single student's cumulative grade
// given the set of summaries.
const gradeSummariesPerStudent = summaries => {
  const groupedGradedSummaries = _.groupBy(
    _.map(summaries, summ => gradeSummary(summ)),
    "user_uuid"
  )
  let average, bucket, name

  return _.map(groupedGradedSummaries, (ggSummaries, uuid) => {
    average = _.sumBy(ggSummaries, "grade") / parseFloat(ggSummaries.length)

    if (average < 1.75) {
      bucket = 1
    } else if (average < 2.5) {
      bucket = 2
    } else {
      bucket = 3
    }

    return {
      uuid,
      summaries: ggSummaries,
      bucket
    }
  })
}

const getPopularIncorrect = (studentData, type) => {
  switch (type) {
    case "Frequency Chart":
      let recordedStudent //current student uuid
      let submissionTally = new Map()
      const submissionEntries = Object.entries(studentData)

      submissionEntries.forEach(([studentUuid, submissions]) => {
        submissions.forEach(entries => {
          const { is_correct, submission_answers } = entries
          if (!is_correct) {
            const answer = JSON.stringify(JSON.parse(submission_answers)["chart_points"]) //re-stringify each answer
            if (submissionTally.has(answer) && recordedStudent != studentUuid) {
              submissionTally.set(answer, submissionTally.get(answer) + 1)
            } else if (!submissionTally.has(answer)) {
              submissionTally.set(answer, 1)
            }
            recordedStudent = recordedStudent != studentUuid ? studentUuid : recordedStudent
          }
        })
      })
      let sortedSubmissions = []
      for (const [entry, count] of submissionTally) {
        sortedSubmissions.push({
          percentage: Math.round((count / submissionEntries.length) * 100),
          answer: JSON.parse(entry)
        })
      }
      return _.orderBy(sortedSubmissions, "percentage", "desc")
    default:
      return
  }
}

export { gradeSummary, gradeSummariesPerStudent, getPopularIncorrect }
